import React, { Component } from 'react'
import { Dispatch } from 'redux'
import axios from 'axios'
import LoaderButton from '../../../components/loaderButton'
import { displayNotification } from '../../../actions/notificationsActions'
import { displaySnakeCaseAsTitleCase } from '../../../helpers/displayFormatHelper'
import { dateTimeFormat } from '../../../helpers/timeHelper'

interface AgreementLinkProps {
  productPackage: ProductPackage,
  agreement: DocumentSubmission,
  dispatch: Dispatch
  provideData?: {
    url: string
    textData: string
  }
}

interface ProductPackage {
  id: string
  customerToken: string
  remittedAt: string | null
  remitError: string | null
  // When remitError was written. Null for an error stored before the column
  // existed, and for a backend that does not send the field yet.
  remitErrorAt?: string | null
  // PBS temporary account number. Walco only, and only after a successful
  // order completion. Support needs it to run GetAccountSummary against the
  // account, and FloPay does not persist it anywhere else.
  walcoAccountNumber?: string | null
  paymentPlanProviderSource?: string | null
}

interface DocumentSubmission {
  docType: string
  viewedAt: string | null
  signedAt: string | null
  createdAt: string
}

export class AgreementLink extends Component<AgreementLinkProps> {
  static displayName = 'AgreementLink'
  
  fetchContractNumber = (products: any []) => {
    if(!products || products.length === 0) return null;
    const productwithContract = products.find(p => p.contractNumber)
    return productwithContract?.contractNumber;
  }

  displaySignedAt = (
    agreement: DocumentSubmission,
    productPackage: ProductPackage,
    dispatch: Dispatch
  ) => {
    if (agreement.signedAt) {
      return dateTimeFormat(agreement.signedAt)
    } else {
      return (
        <>
          <span>signature needed </span>
          <a
            style={{cursor: "pointer"}}
            onClick={() => this.resendAgreement(productPackage, dispatch)}
          >
            resend
          </a>
        </>
      )
    }
  }

  resendAgreement = (
    productPackage: ProductPackage,
    dispatch: Dispatch
  ) => {
    return axios
      .put(
        `${process.env.REACT_APP_BLINKER_API_URL}/api/v3/admin/products/packages/${productPackage.id}/resend_agreements_link`
      )
      .then(() => {
        dispatch(displayNotification('The package link has been sent to the customer'))
      })
      .catch(() => {
        dispatch(displayNotification('Failed to send the package link to the customer', 'error'))
      })
  }

  displayRemittedAt = (agreement: DocumentSubmission, productPackage: ProductPackage) => {
    let remittedAt: string

    if (productPackage.remittedAt) {
      remittedAt = dateTimeFormat(productPackage.remittedAt)

      // The account number belongs to the payment plan, so only that line
      // carries it. A remitted Walco package with no account number means the
      // contract was skipped at FloPay while the order still succeeded --
      // surface it rather than letting it read as a clean remittance.
      const isWalcoPaymentPlan =
        agreement.docType === 'payment_agreement' &&
        productPackage.paymentPlanProviderSource === 'walco'
      if (isWalcoPaymentPlan) {
        return productPackage.walcoAccountNumber ? (
          <span>
            {remittedAt}
            <span className="text-muted"> (Walco #{productPackage.walcoAccountNumber})</span>
          </span>
        ) : (
          <span>
            {remittedAt}
            <span className="text-error" data-testid="walco-account-missing">
              {' '}(no Walco account number -- check the PBS contract)
            </span>
          </span>
        )
      }
    } else if (productPackage.remitError) {
      // The remit date still wins above. A failure shows when it happened, or
      // just that it failed for an error that has no time recorded.
      return (
        <span>
          <span className="text-error" data-testid="remit-failed">
            Failed{productPackage.remitErrorAt ? ` ${dateTimeFormat(productPackage.remitErrorAt)}` : ''}:
          </span>{' '}
          {productPackage.remitError}
        </span>
      )
    } else if (agreement.signedAt) {
      remittedAt = "processing"
    } else {
      remittedAt = "signature needed"
    }

    return <span>{remittedAt}</span>
  }

  render() {
    const { agreement, productPackage, dispatch, provideData } = this.props

    const baseAgreementUrl = `${process.env.REACT_APP_BLINKER_API_URL}/packages/${productPackage.customerToken}/${agreement.docType}`
    const agreementUrl = agreement.signedAt ? `${baseAgreementUrl}/download` : baseAgreementUrl

    const iconName = agreement.signedAt ? "icon-writing-hand" : (
      agreement.viewedAt ? "icon-eye" : "icon-ballot-box"
    )

    return (
      <div>
         {
            <LoaderButton
              type="button"
              className="button button-drive-america continue-application-drive-america"
              onClick={() =>
                window.open(`${provideData.url}${this.fetchContractNumber(productPackage.products)}`, '_blank')
              }
            >
             {provideData.textData}
            </LoaderButton>
          }

        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%"}}>
          <a
            target="_blank"
            className="user-account__link"
            href={agreementUrl}
          >
            View {displaySnakeCaseAsTitleCase(agreement.docType)}
          </a>
          <span className={iconName} style={{textAlign: "right"}}/>
        </div>
        <p className="paragraph--no-bottom-margin col-xs-offset-1">
          Customer Signed: {this.displaySignedAt(agreement, productPackage, dispatch)}
        </p>
        <p className="paragraph--no-bottom-margin col-xs-offset-1">
          Agent Signed: {dateTimeFormat(agreement.createdAt)}
        </p>
        <p className="paragraph--no-bottom-margin col-xs-offset-1">
          {agreement.docType === 'product_agreement' ? "Product" : "Payment Plan"} Remitted: {this.displayRemittedAt(agreement, productPackage)}
        </p>
      </div>
    )
  }
}